/**
 * Saldenabgleich — das Werkzeug zu Schritt 1.
 *
 * Eine Tabelle, je Bankkonto eine Zeile: Anfangs- und Endbestand laut
 * Kontoauszug, daneben laut Abrechnung bzw. Vermögensbericht. Stimmen beide
 * überein, steht ein Haken; weichen sie ab, steht die Differenz. Das ist
 * der ganze Schritt 1 der Checkliste auf einem Blatt.
 *
 * Beide Rollen dürfen schreiben: Die Verwaltung trägt beim Bereitstellen
 * der Auszüge ein, was darauf steht; der Beirat trägt ein, was er liest,
 * und korrigiert, wo er anders liest. Wer zuletzt geschrieben hat, steht
 * unter der Tabelle.
 */

import { useEffect, useState } from 'react';

import { api, datum, eur } from '../../lib/api';
import type { Rolle, Saldenzeile } from '../../lib/typen';

interface Entwurf {
  id?: string;
  konto: string;
  iban: string;
  anfangAuszug: string;
  endeAuszug: string;
  anfangAbrechnung: string;
  endeAbrechnung: string;
}

function zuEntwurf(z: Saldenzeile): Entwurf {
  const t = (n: number | null) => (n === null ? '' : n.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  return {
    id: z.id,
    konto: z.konto,
    iban: z.iban ?? '',
    anfangAuszug: t(z.anfangAuszug),
    endeAuszug: t(z.endeAuszug),
    anfangAbrechnung: t(z.anfangAbrechnung),
    endeAbrechnung: t(z.endeAbrechnung),
  };
}

function zahl(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t.replace(/\s/g, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

const LEER: Entwurf = { konto: '', iban: '', anfangAuszug: '', endeAuszug: '', anfangAbrechnung: '', endeAbrechnung: '' };

export function Salden({
  pruefungId,
  rolle,
  abgeschlossen,
  onMeldung,
  onGeaendert,
}: {
  pruefungId: string;
  rolle: Rolle;
  abgeschlossen: boolean;
  onMeldung: (text: string) => void;
  onGeaendert?: (zeilen: Saldenzeile[]) => void;
}) {
  const [zeilen, setZeilen] = useState<Saldenzeile[] | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf[]>([]);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    api
      .salden(pruefungId)
      .then((z) => {
        setZeilen(z);
        setEntwurf(z.length ? z.map(zuEntwurf) : [{ ...LEER, konto: 'Girokonto' }]);
        if (!z.length && !abgeschlossen) setBearbeiten(true);
      })
      .catch((g) => onMeldung((g as Error).message));
  }, [pruefungId, abgeschlossen, onMeldung]);

  async function speichern() {
    setSpeichert(true);
    try {
      const neu = await api.saldenSpeichern(
        pruefungId,
        entwurf
          .filter((e) => e.konto.trim())
          .map((e) => ({
            id: e.id,
            konto: e.konto.trim(),
            iban: e.iban.trim() || null,
            anfangAuszug: zahl(e.anfangAuszug),
            endeAuszug: zahl(e.endeAuszug),
            anfangAbrechnung: zahl(e.anfangAbrechnung),
            endeAbrechnung: zahl(e.endeAbrechnung),
          })),
      );
      setZeilen(neu);
      setEntwurf(neu.map(zuEntwurf));
      setBearbeiten(false);
      onGeaendert?.(neu);
      const offen = neu.filter((z) => z.anfangStimmt === false || z.endeStimmt === false).length;
      onMeldung(offen ? `Gespeichert — ${offen} Abweichung${offen === 1 ? '' : 'en'}.` : 'Gespeichert — alle Salden stimmen überein.');
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setSpeichert(false);
    }
  }

  function feld(i: number, name: keyof Entwurf, wert: string) {
    setEntwurf((alt) => alt.map((z, j) => (j === i ? { ...z, [name]: wert } : z)));
  }

  if (zeilen === null) return <div className="skelett" style={{ height: 120 }} />;

  const alleStimmen = zeilen.length > 0 && zeilen.every((z) => z.anfangStimmt === true && z.endeStimmt === true);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Saldenabgleich</h3>
        {zeilen.length > 0 && (
          <span className={`pill ${alleStimmen ? 'ok' : 'w'}`}>
            <span className="dot" />
            {alleStimmen ? 'Alle Salden stimmen' : 'Abweichung oder unvollständig'}
          </span>
        )}
        <div className="spacer" />
        {!bearbeiten && !abgeschlossen && (
          <button className="btn sm" onClick={() => setBearbeiten(true)}>
            {zeilen.length ? 'Bearbeiten' : 'Salden eintragen'}
          </button>
        )}
      </div>
      <p className="s" style={{ marginTop: 0 }}>
        Je Bankkonto: die Bestände zum 1. Januar und zum 31. Dezember, einmal vom Kontoauszug und
        einmal aus der Abrechnung bzw. dem Vermögensbericht abgelesen.
        {rolle === 'verwaltung' && ' Als Hausverwaltung können Sie die Werte vorbelegen; der Beirat prüft sie.'}
      </p>

      <div className="tblwrap salden-rahmen">
        <table className="salden">
          <thead>
            <tr>
              <th>Konto</th>
              <th className="num">Anfang lt. Auszug</th>
              <th className="num">Anfang lt. Abrechnung</th>
              <th className="pruef">Abgleich</th>
              <th className="num">Ende lt. Auszug</th>
              <th className="num">Ende lt. Abrechnung</th>
              <th className="pruef">Abgleich</th>
              {bearbeiten && <th />}
            </tr>
          </thead>
          <tbody>
            {bearbeiten
              ? entwurf.map((e, i) => (
                  <tr key={e.id ?? `neu-${i}`}>
                    <td>
                      <input value={e.konto} onChange={(ev) => feld(i, 'konto', ev.target.value)} placeholder="z. B. Girokonto" />
                      <input value={e.iban} onChange={(ev) => feld(i, 'iban', ev.target.value)} placeholder="IBAN (optional)" className="mono klein" />
                    </td>
                    <td><input className="num" value={e.anfangAuszug} onChange={(ev) => feld(i, 'anfangAuszug', ev.target.value)} placeholder="0,00" inputMode="decimal" /></td>
                    <td><input className="num" value={e.anfangAbrechnung} onChange={(ev) => feld(i, 'anfangAbrechnung', ev.target.value)} placeholder="0,00" inputMode="decimal" /></td>
                    <td />
                    <td><input className="num" value={e.endeAuszug} onChange={(ev) => feld(i, 'endeAuszug', ev.target.value)} placeholder="0,00" inputMode="decimal" /></td>
                    <td><input className="num" value={e.endeAbrechnung} onChange={(ev) => feld(i, 'endeAbrechnung', ev.target.value)} placeholder="0,00" inputMode="decimal" /></td>
                    <td />
                    <td>
                      <button className="iconbtn" aria-label="Zeile entfernen" onClick={() => setEntwurf((alt) => alt.filter((_, j) => j !== i))}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))
              : zeilen.map((z) => (
                  <tr key={z.id}>
                    <td>
                      <b>{z.konto}</b>
                      {z.iban && <div className="mono klein" style={{ color: 'var(--tx-3)' }}>{z.iban}</div>}
                    </td>
                    <td className="num">{eur(z.anfangAuszug)}</td>
                    <td className="num">{eur(z.anfangAbrechnung)}</td>
                    <td><Vergleich stimmt={z.anfangStimmt} a={z.anfangAuszug} b={z.anfangAbrechnung} /></td>
                    <td className="num">{eur(z.endeAuszug)}</td>
                    <td className="num">{eur(z.endeAbrechnung)}</td>
                    <td><Vergleich stimmt={z.endeStimmt} a={z.endeAuszug} b={z.endeAbrechnung} /></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {bearbeiten ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={() => setEntwurf((alt) => [...alt, { ...LEER }])}>
            + Konto
          </button>
          <div className="spacer" />
          <button className="btn ghost sm" onClick={() => { setBearbeiten(false); setEntwurf(zeilen.map(zuEntwurf)); }}>
            Abbrechen
          </button>
          <button className="btn primary sm" onClick={speichern} disabled={speichert}>
            {speichert ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      ) : (
        zeilen[0]?.geaendertVon && (
          <p className="note" style={{ marginBottom: 0 }}>
            Zuletzt eingetragen von {zeilen[0].geaendertVon}
            {zeilen[0].geaendertAm ? ` am ${datum(zeilen[0].geaendertAm)}` : ''}.
          </p>
        )
      )}
    </div>
  );
}

function Vergleich({ stimmt, a, b }: { stimmt: boolean | null; a: number | null; b: number | null }) {
  if (stimmt === null) return <span className="chip">unvollständig</span>;
  if (stimmt) return <span className="pill ok"><span className="dot" />stimmt</span>;
  return (
    <span className="pill w" title="Differenz Auszug − Abrechnung">
      <span className="dot" />
      {eur((a ?? 0) - (b ?? 0))}
    </span>
  );
}
